import { createPortal } from 'react-dom'
import { useState } from 'react'
import { Leaf, Warning, Sun, Drop, Thermometer } from '@phosphor-icons/react'
import { SUNLIGHT_ICONS, DIFFICULTY_ICONS, HUMIDITY_LEVEL_ICONS, EnumIcon } from '@/lib/icons'

interface PlantSpecies {
  id: number
  latin_name: string
  common_name_pl: string
  family: string | null
  default_watering_frequency_days: number | null
  default_watering_amount_ml: number | null
  sunlight_preference: string | null
  sunlight_preference_pl: string | null
  difficulty_level: string | null
  difficulty_level_pl: string | null
  temperature_min: number | null
  temperature_max: number | null
  humidity_preference: string | null
  care_tips: string[]
  common_problems: string[]
  source: string | null
  illustration_url: string | null
}

interface Props {
  species: PlantSpecies | null
  onClose: () => void
}

const HUMIDITY_PL: Record<string, string> = {
  low: 'Niska',
  medium: 'Średnia',
  high: 'Wysoka',
}

function IllustrationButton({ species }: { species: PlantSpecies }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'queued' | 'error'>('idle')

  const handleGenerate = async () => {
    setStatus('loading')
    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
      const response = await fetch(`/plant_species/${species.id}/generate_illustration`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken || '',
          'Content-Type': 'application/json',
        },
      })
      if (response.ok) {
        setStatus('queued')
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  if (status === 'queued') {
    return (
      <p className="text-[0.65rem] text-botanical font-label mt-xs text-center">
        Zlecono generowanie &mdash; odśwież stronę za chwilę
      </p>
    )
  }

  if (status === 'error') {
    return (
      <p className="text-[0.65rem] text-danger font-label mt-xs text-center">
        Błąd generowania
      </p>
    )
  }

  const isRegenerate = !!species.illustration_url

  return (
    <button
      onClick={handleGenerate}
      disabled={status === 'loading'}
      className={`w-full mt-xs px-sm py-xs font-label text-[0.65rem] tracking-wide border transition-colors ${
        isRegenerate
          ? 'border-frame/40 text-frame hover:bg-desk/30'
          : 'border-botanical bg-botanical text-paper hover:brightness-110'
      } disabled:opacity-50 disabled:cursor-wait`}
    >
      {status === 'loading'
        ? 'Generowanie...'
        : isRegenerate
          ? 'Regeneruj ilustrację'
          : 'Generuj ilustrację'}
    </button>
  )
}

export function SpeciesDetailModal({ species, onClose }: Props) {
  if (!species) return null

  const requirements: { label: string; value: string; icon?: React.ReactNode }[] = []
  if (species.sunlight_preference_pl) requirements.push({
    label: 'Światło', value: species.sunlight_preference_pl,
    icon: species.sunlight_preference ? <EnumIcon entry={SUNLIGHT_ICONS[species.sunlight_preference]} size={14} /> : null,
  })
  if (species.difficulty_level_pl) requirements.push({
    label: 'Trudność', value: species.difficulty_level_pl,
    icon: species.difficulty_level ? <EnumIcon entry={DIFFICULTY_ICONS[species.difficulty_level]} size={14} /> : null,
  })
  if (species.default_watering_frequency_days) {
    let v = `co ${species.default_watering_frequency_days} dni`
    if (species.default_watering_amount_ml) v += `, ${species.default_watering_amount_ml} ml`
    requirements.push({ label: 'Podlewanie', value: v, icon: <Drop weight="bold" size={14} className="text-water" /> })
  }
  if (species.temperature_min != null && species.temperature_max != null) {
    requirements.push({ label: 'Temperatura', value: `${species.temperature_min}–${species.temperature_max}°C`, icon: <Thermometer weight="bold" size={14} className="text-ink-light" /> })
  }
  if (species.humidity_preference) {
    requirements.push({
      label: 'Wilgotność', value: HUMIDITY_PL[species.humidity_preference] || species.humidity_preference,
      icon: species.humidity_preference ? <EnumIcon entry={HUMIDITY_LEVEL_ICONS[species.humidity_preference]} size={14} /> : null,
    })
  }

  const modal = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-lg"
      style={{ background: 'rgba(58,42,24,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
    >
      <div
        className="w-[56rem] max-w-full max-h-[80vh] flex flex-col animate-[modal-in_0.2s_ease-out] overflow-hidden"
        style={{ background: 'var(--paper)', border: '2px double var(--frame)', boxShadow: '0 8px 32px rgba(58,42,24,0.3)' }}
      >

        <div className="flex-1 overflow-y-auto p-md" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--frame) var(--paper-dark)' }}>
          <div className={`grid gap-md ${species.illustration_url ? 'grid-cols-1 md:grid-cols-[320px_1fr] items-stretch' : 'grid-cols-1'}`}>

            {/* Left: card-like panel — illustration + name + latin */}
            {species.illustration_url ? (
              <div className="flex flex-col" style={{ background: 'var(--paper-dark)', border: '1px solid var(--desk)' }}>
                <div className="aspect-square overflow-hidden">
                  <img
                    src={species.illustration_url}
                    alt={species.common_name_pl}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="px-sm py-xs flex-1 flex flex-col" style={{ borderTop: '1px solid var(--desk)' }}>
                  <h3 className="font-display text-sm uppercase tracking-wider text-ink leading-tight">
                    {species.common_name_pl}
                  </h3>
                  <p className="text-[0.7rem] italic text-frame">{species.latin_name}</p>
                  <div className="mt-auto pt-xs">
                    <IllustrationButton species={species} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-sm">
                <h3 className="font-display text-lg uppercase tracking-wider text-ink leading-tight">
                  {species.common_name_pl}
                </h3>
                <p className="text-xs italic text-frame mt-[2px]">
                  {species.latin_name}
                  {species.family && <span className="text-frame/50"> &middot; {species.family}</span>}
                </p>
                <IllustrationButton species={species} />
              </div>
            )}

            {/* Right: all details */}
            <div>
              {/* Requirements */}
              <div
                className="p-sm mb-md"
                style={{ background: 'var(--paper-dark)', border: '1px solid var(--desk)' }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 1rem' }}>
                  {requirements.map((r, i) => (
                    <div key={i}>
                      <div className="font-label text-[0.6rem] uppercase tracking-wide text-frame mb-[2px]">{r.label}</div>
                      <div className="text-sm text-ink inline-flex items-center gap-xs">
                        {r.icon}
                        {r.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Care tips */}
              {species.care_tips.length > 0 && (
                <div className="mb-md">
                  <div className="flex items-center gap-xs mb-sm">
                    <Leaf weight="bold" size={14} className="text-botanical" />
                    <h4 className="font-label text-xs uppercase tracking-wide text-botanical">Pielęgnacja</h4>
                  </div>
                  <div className="space-y-[2px]">
                    {species.care_tips.map((tip, i) => (
                      <p key={i} className="text-xs text-ink leading-relaxed flex items-baseline gap-xs">
                        <span className="text-botanical/50 shrink-0">&bull;</span>
                        <span>{tip}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Common problems */}
              {species.common_problems.length > 0 && (
                <div>
                  <div className="flex items-center gap-xs mb-sm">
                    <Warning weight="bold" size={14} className="text-danger" />
                    <h4 className="font-label text-xs uppercase tracking-wide text-danger">Typowe problemy</h4>
                  </div>
                  <div className="space-y-[2px]">
                    {species.common_problems.map((problem, i) => (
                      <p key={i} className="text-xs text-ink leading-relaxed flex items-baseline gap-xs">
                        <span className="text-danger/40 shrink-0">&bull;</span>
                        <span>{problem}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
